import React, { FC, HTMLInputTypeAttribute, useCallback, useMemo } from "react";
import debounce from "lodash/debounce";
import styled from "styled-components";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface InputProps {
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  endText?: string;
  style?: React.CSSProperties;
  placeholder?: string;
  icon?: IconProp;
  type?: HTMLInputTypeAttribute;
}

const InputContainer = styled.div`
  width: 320px;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;

  box-shadow: 0 0 0 0 rgb(19 124 189 / 0%), 0 0 0 0 rgb(19 124 189 / 0%),
    inset 0 0 0 1px rgb(16 22 26 / 15%), inset 0 1px 1px rgb(16 22 26 / 20%);
  border-radius: 16px;
  padding-left: 8px;
  padding-right: 8px;
  height: 32px;

  :focus-within {
    background: var(--global-background-color);
  }
`;

const InputElement = styled.input`
  margin: 0 0.4em;
  width: 100%;
  border: none;
  background: none;

  &:focus {
    outline: none;
  }
`;

const InputAfterText = styled.span`
  color: #777777;
  font-weight: 200;
  font-size: var(--global-font-size-xs);
  margin-bottom: -0.2rem;
  white-space: nowrap;
`;
export const Input: FC<InputProps> = ({
  style,
  onChange,
  endText,
  placeholder,
  icon,
  type,
}) => {
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event);
    },
    [onChange]
  );

  const debouncedOnChange = useMemo(
    () => debounce(handleChange, 300),
    [handleChange]
  );

  return (
    <InputContainer style={style}>
      {icon && <FontAwesomeIcon icon={icon} />}
      <InputElement
        data-testid="InputComponent"
        placeholder={placeholder}
        onChange={debouncedOnChange}
        type={type}
        autoFocus
      />
      {endText && (
        <InputAfterText data-testid="InputAfterText">{endText}</InputAfterText>
      )}
    </InputContainer>
  );
};
